import { useEffect, useRef, useState } from "react";
import { toPersianDigits } from "./ReaderToolbar";

type Props = {
  page: number;
  pageCount: number;
  scrollRootSelector?: string;
};

/**
 * Custom scrollbar: hidden by default, activates on touch / pointer in the
 * track area; shows current page number in the thumb.
 */
export function PageScrollbar({
  page,
  pageCount,
  scrollRootSelector = ".canvas-scroll",
}: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  const [ratio, setRatio] = useState(0);
  const hideTimer = useRef(0);
  const dragging = useRef(false);

  const bump = () => {
    setActive(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      if (!dragging.current) setActive(false);
    }, 1400);
  };

  useEffect(() => {
    const root = document.querySelector(scrollRootSelector) as HTMLElement | null;
    if (!root) return;

    const sync = () => {
      const max = root.scrollHeight - root.clientHeight;
      setRatio(max > 0 ? root.scrollTop / max : 0);
    };
    sync();

    const onScroll = () => {
      sync();
      bump();
    };
    root.addEventListener("scroll", onScroll, { passive: true });
    root.addEventListener("touchstart", bump, { passive: true });
    return () => {
      root.removeEventListener("scroll", onScroll);
      root.removeEventListener("touchstart", bump);
      window.clearTimeout(hideTimer.current);
    };
  }, [scrollRootSelector, pageCount]);

  const jumpTo = (clientY: number) => {
    const track = trackRef.current;
    const root = document.querySelector(
      scrollRootSelector
    ) as HTMLElement | null;
    if (!track || !root) return;
    const rect = track.getBoundingClientRect();
    const t = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
    const max = root.scrollHeight - root.clientHeight;
    root.scrollTop = max * t;
    setRatio(t);
  };

  if (pageCount < 1) return null;

  const label = toPersianDigits(`${page} / ${pageCount}`);
  const thumbTop = `calc(${ratio * 100}% - ${ratio * 36}px)`;

  return (
    <div
      ref={trackRef}
      className={`page-scrollbar${active ? " active" : ""}`}
      role="scrollbar"
      aria-valuenow={page}
      aria-valuemin={1}
      aria-valuemax={pageCount}
      aria-label="نوار صفحه"
      onPointerDown={(e) => {
        dragging.current = true;
        bump();
        trackRef.current?.setPointerCapture(e.pointerId);
        jumpTo(e.clientY);
      }}
      onPointerMove={(e) => {
        if (!dragging.current) return;
        jumpTo(e.clientY);
      }}
      onPointerUp={() => {
        dragging.current = false;
        bump();
      }}
      onPointerCancel={() => {
        dragging.current = false;
        bump();
      }}
    >
      <div className="page-scrollbar-thumb" style={{ top: thumbTop }}>
        <span>{label}</span>
      </div>
    </div>
  );
}

/** Auto-hiding bottom-center page indicator (PDF-viewer style). */
export function PageHud({ page, pageCount }: { page: number; pageCount: number }) {
  const [visible, setVisible] = useState(true);
  const timer = useRef(0);

  useEffect(() => {
    setVisible(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setVisible(false), 1600);
    return () => window.clearTimeout(timer.current);
  }, [page, pageCount]);

  useEffect(() => {
    const root = document.querySelector(".canvas-scroll") as HTMLElement | null;
    if (!root) return;
    const show = () => {
      setVisible(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setVisible(false), 1600);
    };
    root.addEventListener("scroll", show, { passive: true });
    root.addEventListener("touchstart", show, { passive: true });
    return () => {
      root.removeEventListener("scroll", show);
      root.removeEventListener("touchstart", show);
    };
  }, []);

  if (pageCount < 1) return null;
  return (
    <div
      className={`page-hud${visible ? " visible" : ""}`}
      role="status"
      aria-live="polite"
    >
      {toPersianDigits(`${page} / ${pageCount}`)}
    </div>
  );
}
